import { ensureDefined, firstDefined } from '@avantia/client-and-server-utilities';
import { createControlId } from '@avantia/questionset-model';
import { HpInputControlProps } from '../../../types/inputControlTypes';
import { StandardFormGroupWidget } from '../../widgets/standardFormGroupWidget';
import { createSpaceKeyAsClickHandler } from '../controlEvents';
import { createEqualityComparer, iterateLookupMap } from '../controlUtilities';
import { createStandardMouseEvent } from '../inputControl';
import { CheckBoxItem, HpCheckBoxItemProps } from './checkBoxControl';

export type CheckBoxListControlProps = HpInputControlProps;

export function CheckBoxListControl(props: CheckBoxListControlProps): JSX.Element {
  return CheckBoxListControlWithCustomContent(props, (optionProps) => (
    <CheckBoxItem key={optionProps.id} {...optionProps} />
  ));
}

export type HpCustomCheckBoxContentFunction = (props: HpCheckBoxItemProps) => JSX.Element;

export function CheckBoxListControlWithCustomContent(
  {
    id,
    name,
    value,
    answerSource,
    data,
    onChange,
    onClick,
    disabled,
    dataType,
    errors,
    question,
    ui,
    questionText,
    subtext
  }: CheckBoxListControlProps,
  CustomCheckBoxContent: HpCustomCheckBoxContentFunction,
  optionsClass = ''
): JSX.Element {
  const { isMultiSelect } = question;
  const { lookup } = data;
  const error = errors ? errors[id] : undefined;
  const options: JSX.Element[] = [];
  const textAndStyle = data.textAndStyle.standard;
  const { layout } = textAndStyle;
  const isSelected = createEqualityComparer(dataType, isMultiSelect, id);

  if (lookup) {
    const onClickFunc = createStandardMouseEvent(onClick, question) as any;
    const onKeyPressFunc = createSpaceKeyAsClickHandler(onClickFunc);
    iterateLookupMap(lookup, ui, (option, key) => {
      const optionText = ensureDefined(firstDefined(option ? option.text : null, key), ' optionText');
      const itemKey = createControlId({ questionId: id, key });
      const optionProps: HpCheckBoxItemProps = {
        itemKey,
        name,
        id: key,
        optionText,
        disabled,
        isChecked: isSelected(key, value),
        onClick: onClickFunc,
        onKeyPress: onKeyPressFunc
      };

      options.push(CustomCheckBoxContent(optionProps));
    });
  }

  let groupClassName: string;
  switch (layout) {
    case 'float':
      groupClassName = 'hp-float';
      break;
    default:
      groupClassName = '';
      break;
  }

  if (optionsClass && groupClassName) {
    groupClassName = ` ${groupClassName}`;
  }

  return (
    <StandardFormGroupWidget
      id={id}
      question={question}
      questionText={questionText}
      control={
        <div role="group" className={optionsClass + groupClassName}>
          {options}
        </div>
      }
      textAndStyle={textAndStyle}
      error={error}
      answerSource={answerSource}
      controlType="checkbox-list"
      dataType={dataType}
      helpInfo={data.helpInfo}
      helpInfoUI={ui.helpInfo}
      subtext={subtext}
      onClick={onChange}
    />
  );
}
